import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { PublicUserEntity } from './public-user.entity';
import { tinyintBoolean } from './tinyint-boolean.transformer';

/**
 * PD-16475 — a storefront user's master notification controls and text
 * message consent. One row per user, created the first time any of these is
 * set.
 *
 * Consent and opt-out are stored, not inferred, because texting without them
 * is a compliance problem rather than a preference problem:
 *   - consent is recorded (phone + time) when the user submits the support
 *     form or saves a notification control with Text ticked, next to the
 *     texting disclosure (PORTAL_SMS_DISCLOSURE);
 *   - an opt-out is recorded when Twilio refuses a send because the recipient
 *     replied STOP (error 21610) or when a STOP reply reaches one of the
 *     tenant's numbers; a START reply clears it.
 * A text is sent only when consent exists for the account's current phone and
 * no opt-out applies to it.
 */
@Index('public_user_notification_setting_user_idx', ['fkPublicUserId'], {
	unique: true,
})
@Index('public_user_notification_setting_consent_phone_idx', [
	'smsConsentPhone',
])
@Index('public_user_notification_setting_opted_out_phone_idx', [
	'smsOptedOutPhone',
])
@Entity('public_user_notification_setting')
export class PublicUserNotificationSettingEntity extends BaseEntity {
	@Column('int', { name: 'fk_public_user__id', unsigned: true })
	public fkPublicUserId!: number;

	/** Master switch: when set nothing is delivered on any channel. */
	@Column('tinyint', {
		name: 'notificationsPaused',
		width: 1,
		default: () => "'0'",
		transformer: tinyintBoolean,
	})
	public notificationsPaused!: boolean;

	/** The E.164 phone the user agreed to receive texts on. */
	@Column('varchar', { name: 'smsConsentPhone', length: 20, nullable: true })
	public smsConsentPhone!: string | null;

	@Column('datetime', { name: 'smsConsentDate', nullable: true })
	public smsConsentDate!: Date | null;

	/** When the user opted out of texts (STOP). Null = not opted out. */
	@Column('datetime', { name: 'smsOptedOutDate', nullable: true })
	public smsOptedOutDate!: Date | null;

	/** The E.164 phone that opted out. */
	@Column('varchar', { name: 'smsOptedOutPhone', length: 20, nullable: true })
	public smsOptedOutPhone!: string | null;

	/**
	 * The number the STOP applies to (the tenant or Workd number that received
	 * it, or that the refused text was sent from) — the number the user must
	 * text START to, since the app cannot lift a carrier block itself.
	 */
	@Column('varchar', {
		name: 'smsOptedOutSenderPhone',
		length: 20,
		nullable: true,
	})
	public smsOptedOutSenderPhone!: string | null;

	/**
	 * PD-14306 — when the user answered "No" to the one-time question, asked
	 * the first time they click "Notify me" on an out-of-stock item, whether
	 * they also want a text when it is back. Null = never declined. Once set
	 * the question is not asked again; the user can still turn Text on for
	 * back-in-stock notices in their notification settings.
	 */
	@Column('datetime', {
		name: 'backInStockSmsPromptDeclinedDate',
		nullable: true,
	})
	public backInStockSmsPromptDeclinedDate!: Date | null;

	@ManyToOne(() => PublicUserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_public_user__id', referencedColumnName: 'id' }])
	public fkPublicUser!: PublicUserEntity;
}
